import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DropdownButton } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import Swal from "sweetalert2";

import StatusButton from "@modules/Trees/components/Button/StatusButton";
import TreeImageForm from "@modules/Trees/components/Form/AddForm/TreeImageForm";
import ReactTable from "@components/ReactTable";
import { AngleDownIcon, EditIcon, TrashIcon, ImageAddIconSmall } from "@components/icons";
import { deleteTree } from "@modules/Trees/services/treeCrud";
import { statusList } from "@helper/healthState";
import { treeStatusHeader } from "@modules/Trees/components/TreeList";

export default function TreeList({
  trees,
  setFormValue,
  setOpenImageForm,
  openImageForm,
  setIsUpdate,
  setOpenForm,
  formValue,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [filterTree, setFilterTree] = useState(trees);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [ordering, setOrdering] = useState("");
  const tid = query.get("selectedTree");

  // React Query client and mutation for deleting trees
  const queryClient = useQueryClient();
  const { mutate: treeDelete } = useMutation(deleteTree, {
    onSuccess: () => {
      queryClient.invalidateQueries("trees-without-project");
      Swal.fire("Deleted!", "Tree has been deleted.", "success");
    },
    onError: () => {
      Swal.fire("Error!", "Failed to delete tree.", "error");
    },
  });

  // Handle deleting tree
  const handleDelete = (treeId) => {
    Swal.fire({
      html: '<p className="text-center">Are you sure you want to delete this tree?</p>',
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#388090",
      cancelButtonColor: "#EF3600",
      cancelButtonText: "Cancel",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        treeDelete(treeId);
      }
    });
  };

  useEffect(() => {
    if (selectedStatus.length > 0) {
      let b = trees.filter((tree) => selectedStatus.includes(tree.health_state));
      setFilterTree(b);
    } else {
      setFilterTree(trees);
    }
  }, [selectedStatus, trees]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const filterTreesByStatus = (status) => {
    const findIdx = selectedStatus.indexOf(status);
    if (findIdx > -1) {
      let a = selectedStatus.filter((findIdx) => findIdx !== status);
      setSelectedStatus(a);
    } else {
      setSelectedStatus((prev) => [...prev, status]);
    }
    setPage(1);
  };

  const searchedTrees = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return filterTree;

    return filterTree.filter((tree) => {
      const haystack = [tree.name, tree.area, tree.tree_cadastre_no]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [filterTree, search]);

  const sortedTrees = useMemo(() => {
    if (!ordering) return searchedTrees;

    const isDesc = ordering.startsWith("-");
    const key = isDesc ? ordering.slice(1) : ordering;
    const direction = isDesc ? -1 : 1;

    return [...searchedTrees].sort((a, b) => {
      const aValue = a?.[key] ?? "";
      const bValue = b?.[key] ?? "";

      if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * direction;
      }

      return (
        String(aValue).localeCompare(String(bValue), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * direction
      );
    });
  }, [searchedTrees, ordering]);

  const pagedTrees = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedTrees.slice(start, start + pageSize);
  }, [sortedTrees, page, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(sortedTrees.length / pageSize));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [sortedTrees.length, pageSize, page]);

  const columns = useMemo(
    () => [
      {
        Header: `${t("tree")}`,
        accessor: "name",
        Cell: (props) => {
          let name = `${props.row.original.name}`;
          return (
            <div className="d-flex align-items-center gap-3 w-100">
              <div>
                <h5 className="mb-0">{name}</h5>
              </div>
            </div>
          );
        },
      },
      {
        Header: `${t("area")}`,
        accessor: "area",
      },
      treeStatusHeader(t),
      {
        Header: `${t("actions")}`,
        accessor: "actions",
        style: { width: "80px", textAlign: "center" },
        disableSortBy: true,
        Cell: (props) => {
          return (
            <div className="text-center">
              <span title="Edit">
                <EditIcon
                  onClick={() => {
                    setIsUpdate(true);
                    setOpenForm(true);
                    setFormValue(props.row.original);
                  }}
                />
              </span>
              <span title="Upload Image">
                <ImageAddIconSmall
                  onClick={() => {
                    setOpenImageForm(true);
                    setFormValue(props.row.original);
                  }}
                />
              </span>
              <span title="Delete">
                <TrashIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    let treeId = props.row.original.id;
                    handleDelete(treeId);
                  }}
                />
              </span>
            </div>
          );
        },
      },
    ],
    [t]
  );

  const onRowClicked = (row) => {
    const { id } = row.original;
    const params = new URLSearchParams(location.search);
    params.set("selectedTree", id);
    navigate(`${location.pathname}?${params.toString()}`);
  };

  return (
    <>
      <ReactTable
        columns={columns}
        data={pagedTrees}
        onRowClicked={onRowClicked}
        activeId={tid}
        pagesize={pageSize}
        size={[10, 20, 30, 40, 50]}
        manualPagination
        pageCount={Math.max(1, Math.ceil(sortedTrees.length / pageSize))}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setPage(1);
        }}
        onSortChange={(nextOrdering) => {
          setOrdering(nextOrdering);
          setPage(1);
        }}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={t("search_tree")}
        moreFilter={
          <>
            <DropdownButton
              title={
                <div className="d-flex gap-2">
                  <span>{t("status")}</span>
                  <AngleDownIcon />
                </div>
              }
              variant="btn-transparent"
            >
              {statusList.map((status, i) => (
                <li key={i} className="d-flex align-items-center gap-2 px-2 py-1">
                  <input
                    type="checkbox"
                    onChange={() => filterTreesByStatus(status.status)}
                    checked={selectedStatus.includes(status.status)}
                  />
                  <StatusButton status={status.status} width="12px" height="12px"></StatusButton>{" "}
                  {t(status.label)}
                </li>
              ))}
            </DropdownButton>
          </>
        }
      />
      {openImageForm && (
        <TreeImageForm show={openImageForm} setOpenForm={setOpenImageForm} formValue={formValue} />
      )}
    </>
  );
}
